import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/ui/Icon.js';
import { CATEGORY_ORDER, CATEGORY_LABEL, CATEGORY_ICON } from '../utils/categories.js';

interface ChecklistItem {
  ingredientId: string;
  name: string;
  quantity: number;
  unit: string;
  category: string;
}

interface ChecklistResponse {
  items: ChecklistItem[];
  haveItIds: string[];
}

function groupByCategory(items: ChecklistItem[]) {
  const buckets = new Map<string, ChecklistItem[]>();
  for (const item of items) {
    const cat = CATEGORY_LABEL[item.category] ? item.category : 'other';
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat)!.push(item);
  }
  return CATEGORY_ORDER
    .filter((c) => buckets.has(c))
    .map((c) => ({ category: c, items: buckets.get(c)!.sort((a, b) => a.name.localeCompare(b.name)) }));
}

export function IngredientChecklistPage() {
  const { mealPlanId } = useParams<{ mealPlanId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const queryKey = ['checklist-items', mealPlanId];

  const { data, isLoading, isError, refetch } = useQuery<ChecklistResponse>({
    queryKey,
    queryFn: () => api.get(`/api/meal-plans/${mealPlanId}/checklist-items`),
    enabled: !!mealPlanId,
  });

  const haveItIds = useMemo(
    () => new Set(data?.haveItIds ?? []),
    [data?.haveItIds],
  );

  const toggleMutation = useMutation({
    mutationFn: (vars: { ingredientId: string; haveIt: boolean }) =>
      api.patch<{ haveItIds: string[] }>(`/api/meal-plans/${mealPlanId}/checklist`, vars),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<ChecklistResponse>(queryKey);
      if (prev) {
        const ids = new Set(prev.haveItIds);
        if (vars.haveIt) ids.add(vars.ingredientId);
        else ids.delete(vars.ingredientId);
        qc.setQueryData<ChecklistResponse>(queryKey, { ...prev, haveItIds: Array.from(ids) });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey });
    },
  });

  function toggleHaveIt(ingredientId: string) {
    const haveIt = !haveItIds.has(ingredientId);
    toggleMutation.mutate({ ingredientId, haveIt });
  }

  const buildMutation = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/api/shopping-lists/generate', { mealPlanId }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['shopping-list-for-plan', mealPlanId] });
      navigate(`/shopping-list/${res.id}`);
    },
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-3 p-6">
          <div className="h-12 rounded-lg w-1/2" style={{ background: 'var(--surface-2)' }} />
          <div className="h-24 rounded-lg" style={{ background: 'var(--surface-2)' }} />
          <div className="h-64 rounded-lg" style={{ background: 'var(--surface-2)' }} />
        </div>
      </Layout>
    );
  }

  if (isError || !data) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-sm" style={{ color: 'var(--danger, #e11d48)' }}>
            Could not load ingredients.{' '}
            <button onClick={() => refetch()} className="underline" style={{ color: 'var(--accent)' }}>
              Retry
            </button>
          </p>
        </div>
      </Layout>
    );
  }

  const items = data.items;
  const grouped = groupByCategory(items);
  const haveCount = items.filter((i) => haveItIds.has(i.ingredientId)).length;
  const total = items.length;
  const remaining = total - haveCount;
  const building = buildMutation.isPending;

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <div
            className="text-xs font-semibold tracking-[0.16em] uppercase mb-2"
            style={{ color: 'var(--accent)' }}
          >
            Weekly plan · Check your kitchen
          </div>
          <h1 className="h-display" style={{ margin: 0, fontSize: 44, lineHeight: 1.05, color: 'var(--ink)' }}>
            What do you already <span className="h-serif" style={{ fontStyle: 'italic', color: 'var(--accent)' }}>have?</span>
          </h1>
          <p className="text-sm mt-3" style={{ color: 'var(--ink-3)', maxWidth: 560 }}>
            Tap "I have it" for anything already in your kitchen. We'll leave those off your shopping list.
          </p>
        </div>

        {total > 0 && (
          <div className="card p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-semibold tracking-wider uppercase mb-1" style={{ color: 'var(--ink-3)' }}>
                Marked as on-hand
              </div>
              <div className="h-display" style={{ fontSize: 28, lineHeight: 1, color: 'var(--ink)' }}>
                {haveCount}<span style={{ color: 'var(--ink-4)', fontSize: 20 }}> / {total}</span>
              </div>
            </div>
            <button
              onClick={() => buildMutation.mutate()}
              disabled={building}
              className="btn btn-primary"
              style={{ padding: '12px 22px', fontSize: 14 }}
            >
              {building ? 'Building…' : 'Build my shopping list'}
              {!building && remaining < total && (
                <span style={{ marginLeft: 6, opacity: 0.85 }}>({remaining} to buy)</span>
              )}
              <Icon name="arrow" size={14} />
            </button>
          </div>
        )}

        {buildMutation.isError && (
          <p className="text-sm mb-4" style={{ color: 'var(--danger, #e11d48)' }}>
            {(buildMutation.error as any)?.body?.error ?? 'Failed to build shopping list.'}
          </p>
        )}

        {total === 0 ? (
          <div className="card px-6 py-10 text-center">
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>No ingredients found for this plan.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(({ category, items: catItems }) => {
              const sectionHave = catItems.filter((i) => haveItIds.has(i.ingredientId)).length;
              return (
                <section key={category}>
                  <header className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <Icon name={CATEGORY_ICON[category] ?? 'cart'} size={14} style={{ color: 'var(--accent)' }} />
                      <h2 className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: 'var(--ink-2)' }}>
                        {CATEGORY_LABEL[category] ?? category}
                      </h2>
                    </div>
                    <span className="text-[11px] font-mono" style={{ color: 'var(--ink-3)' }}>
                      {sectionHave} / {catItems.length} on hand
                    </span>
                  </header>

                  <ul className="card divide-y" style={{ borderColor: 'var(--line-soft)' }}>
                    {catItems.map((item) => {
                      const have = haveItIds.has(item.ingredientId);
                      return (
                        <li key={item.ingredientId} style={{ borderColor: 'var(--line-soft)' }}>
                          <button
                            onClick={() => toggleHaveIt(item.ingredientId)}
                            className="w-full flex items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-[var(--surface-2)]"
                            aria-pressed={have}
                          >
                            <span
                              className="flex items-center justify-center w-5 h-5 rounded-md flex-shrink-0 transition-all"
                              style={{
                                background: have ? 'var(--accent)' : 'transparent',
                                border: `1.5px solid ${have ? 'var(--accent)' : 'var(--line-soft)'}`,
                              }}
                            >
                              {have && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent-ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 12.5L10 17l9-10" />
                                </svg>
                              )}
                            </span>

                            <div className="flex-1 min-w-0">
                              <div
                                className="text-sm font-medium truncate"
                                style={{ color: have ? 'var(--ink-4)' : 'var(--ink)' }}
                              >
                                {item.name}
                              </div>
                              <div className="text-xs mt-0.5" style={{ color: 'var(--ink-3)' }}>
                                {item.quantity} {item.unit}
                              </div>
                            </div>

                            <span
                              className="text-[11px] font-semibold uppercase tracking-wider"
                              style={{ color: have ? 'var(--accent)' : 'var(--ink-4)' }}
                            >
                              {have ? 'have it' : 'I have it'}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}

            <div className="flex justify-end pt-2">
              <button
                onClick={() => buildMutation.mutate()}
                disabled={building}
                className="btn btn-primary"
                style={{ padding: '14px 24px', fontSize: 14 }}
              >
                {building ? 'Building…' : 'Build my shopping list'} <Icon name="arrow" size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
